import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Popover } from 'react-tiny-popover';

import { FormAPI } from 'lecom-api';
import { formatDatetime, formatToNumber } from 'utils/formatting_utils';
import FlashMessages from '../../../../flash_message/components/flash_messages.jsx';
import { InputGroup } from '../../../../form/components/form/input_group';
import FormFieldTypes from '../../../../form/constants/form_field_types';
import { FormEvents } from '../../../../form/events/form_event_handler';
import ButtonGroup from '../../../../realize_overrides/components/button/button_group.jsx';
import Modal from '../../../../realize_overrides/components/modal/modal.jsx';
import { ModalActions } from '../../../../realize_overrides/stores/modal_store';
import { generateUUID } from '../../../../utils/uuid';
import DocumentModalIds from '../../../constants/document_modal_ids';
import DocumentSubmitDataContainers from '../../../constants/document_submit_data_containers';
import DocumentValidationsErrors from '../../../constants/document_validations_errors';
import { DocumentActions } from '../../../reflux/document_store';

import './styles.scss';

const Realize = {
  i18n: window.Realize.i18n,
  Modal: Modal,
  ModalHeader: window.ModalHeader,
  ModalContent: window.ModalContent,
  ModalFooter: window.ModalFooter,
};

const ImportMultipleModal = React.memo(({ fields, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filesList, setFilesList] = useState([]);
  const [popoversOpen, setPopoversOpen] = useState([]);
  const [startQueue, setStartQueue] = useState(false);
  const [countFileImported, setCountFileImported] = useState(0);

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
  } = useDropzone({ disabled: startQueue });

  const acceptStyle = {
    color: '#1976D2',
    borderColor: '#1976D2',
  };

  const rejectStyle = {
    color: '#ff1744',
    borderColor: '#ff1744',
  };

  const style = useMemo(
    () => ({
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject]
  );

  const importFiles = () => {
    const modalContentElem = document.querySelector(
      '#importMultipleModal .modal-content-container'
    );

    if (modalContentElem) {
      modalContentElem.scrollTo(0, 0);
    }

    setFilesList(filesList.map((f) => ({ ...f, metadataVisible: false })));
    setStartQueue(true);
  };

  const dismissModal = () => {
    ModalActions.close({ modalId: 'importMultipleModal' });

    setTimeout(() => {
      setIsOpen(false);
    }, 500);
  };

  const buildDismissButtonProps = () => {
    return {
      name: Realize.i18n.t('actions.close'),
      element: 'a',
      style: 'cancel',
      role: 'button',
      icon: 'close',
      onClick: dismissModal,
      className: `button-close-modal-document ${startQueue ? 'disabled' : ''}`,
      disabled: startQueue,
    };
  };

  const buildImportButtonProps = () => {
    return {
      name: Realize.i18n.t('exportMultipleModal.actions.load'),
      element: 'a',
      style: 'primary',
      role: 'button',
      icon: 'file_upload',
      onClick: importFiles,
      className: `button-import-document ${startQueue ? 'disabled' : ''}`,
      disabled: startQueue,
    };
  };

  const buildButtonGroupProps = () => {
    return [buildDismissButtonProps(), buildImportButtonProps()];
  };

  const renderModalHeader = () => {
    return (
      <span className="h5-custom">
        {Realize.i18n.t('importMultipleModal.modalTitle')}
      </span>
    );
  };

  const renderModalButtons = () => {
    return <ButtonGroup buttons={buildButtonGroupProps()} />;
  };

  const removeFile = (id) => {
    setFilesList(filesList.filter((f) => f.id !== id));
  };

  const changeDocumentField = useCallback(
    (e, id) => {
      setFilesList(
        filesList.map((f) => {
          if (f.id === id) {
            if (e.target.value) {
              const documentField = getDocumentStore(e.target.value);
              const mappingFields = documentField.inputProps.formFieldMappings;
              const formApi = FormAPI.form(documentField.inputProps.formId);
              const metadataValues = (mappingFields || []).reduce(
                (acc, cur) => {
                  const fieldApi = cur.formField.gridFormParent
                    ? formApi
                        .fields(cur.formField.gridFormParent)
                        .fields(cur.formField.id)
                    : formApi.fields(cur.formField.id);

                  return {
                    ...acc,
                    [`${cur.documentField}_${documentField.inputId}_IMPORT_templateField`]: fieldApi.value(),
                  };
                },
                {}
              );

              return { ...f, documentFieldId: e.target.value, metadataValues };
            }

            return { ...f, documentFieldId: e.target.value };
          }

          return f;
        })
      );
    },
    [filesList]
  );

  const renderSelectDocumentFields = (id, documentFieldId) => {
    const documentStore = getDocumentStore();
    let documentFields = [];

    for (const key in documentStore) {
      documentFields = [...documentFields, documentStore[key]];
    }

    const documentFieldsFormatted = documentFields.reduce((acc, cur) => {
      if (
        filesList.find((f) => f.documentFieldId === cur.inputId) &&
        cur.inputId !== documentFieldId
      ) {
        return acc;
      }

      const gridFormParent = cur.gridData.gridIdentifier;
      const formApi = FormAPI.form(cur.inputProps.formId);
      const fieldDocumentApi = gridFormParent
        ? formApi.fields(gridFormParent).fields(cur.inputProps.id)
        : formApi.fields(cur.inputProps.id);

      if (
        !cur.inputProps.canImport ||
        fieldDocumentApi.readOnly() ||
        !fieldDocumentApi.visible() ||
        fieldDocumentApi.disabled()
      ) {
        return acc;
      }

      return [...acc, cur];
    }, []);

    return (
      <select
        onChange={(e) => changeDocumentField(e, id)}
        value={documentFieldId}
        className={`select-document-field`}
        disabled={startQueue}
        tabIndex="0"
        data-testid='select-document-field-value'
      >
        <option value="">
          {Realize.i18n.t('importMultipleModal.selectDocumentFieldsDefault')}
        </option>
        {documentFieldsFormatted.map((d) => (
          <option value={d.inputId} key={d.inputId}>
            {d.inputProps.label}
          </option>
        ))}
      </select>
    );
  };

  const hasMetadata = (documentFieldId) => {
    const documentField = getDocumentStore(documentFieldId);

    if (documentFieldId && documentField) {
      return !!Object.keys(documentField.template.fields).length;
    }

    return false;
  };

  const getDocumentStore = (documentFieldId) => {
    const documentStore = Lecom.stores.DocumentStore.store || {};

    if (documentFieldId) {
      return documentStore[documentFieldId] || {};
    }

    return documentStore;
  };

  const modalReady = () => {
    setFilesList(filesList.filter((f) => !f.successImport));
    setIsOpen(true);
  };

  const toggleVisibleMetadata = (id) => {
    setFilesList(
      filesList.map((f) => {
        if (f.id === id) {
          return { ...f, metadataVisible: !f.metadataVisible };
        }

        return f;
      })
    );
  };

  const changePopoversOpen = (fileId, isOpen) => {
    if (popoversOpen.find((p) => p.fileId === fileId)) {
      setPopoversOpen(
        popoversOpen.map((p) => {
          if (p.fileId === fileId) {
            return { ...p, isOpen };
          }

          return p;
        })
      );
    } else {
      setPopoversOpen([...popoversOpen, { fileId, isOpen }]);
    }
  };

  const getPopoverIsOpen = (fileId) => {
    const popover = popoversOpen.find((p) => p.fileId === fileId);
    return popover ? popover.isOpen : false;
  };

  const contentPopoverError = (file) => {
    const { error } = file;
    const documentField = getDocumentStore(file.documentFieldId);
    let errorFormatted = error.message;

    if (errorFormatted === DocumentValidationsErrors.DUPLICATED_DOCUMENTS) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.duplicatedDocuments'
      );
    } else if (
      errorFormatted === DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN
    ) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.divergentVersionInCheckin'
      );
    } else if (errorFormatted === DocumentValidationsErrors.CHECKOUT_UNDONE) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.checkoutUndone'
      );
    } else if (error.code === 'file_type_restriction_exception') {
      errorFormatted = Realize.i18n
        .t(
          `importMultipleModal.filesListErrors.file_type_restriction_exception`
        )
        .replace('@', file.data.name.split('.').pop());
    } else if (error.code === 'file_size_restriction_exception') {
      errorFormatted = Realize.i18n
        .t(
          `importMultipleModal.filesListErrors.file_size_restriction_exception`
        )
        .replace('@', documentField.template.maxFileSizeAllowed);
    } else if (error.code) {
      errorFormatted = Realize.i18n.t(
        `importMultipleModal.filesListErrors.${error.code}`
      );
    }

    return (
      <div className="content-popover-error">
        <ul>
          <li>{errorFormatted}</li>
        </ul>
      </div>
    );
  };

  const importFileByQueue = (index) => {
    const file = filesList[index];
    const documentField = getDocumentStore(file.documentFieldId);
    const hasErrorEmptyDocuments = !file.documentFieldId;
    const hasErrorAlreadyFileLinked = !hasErrorEmptyDocuments
      ? !!documentField.document.id
      : false;
    const metadataFields =
      documentField.template && documentField.template.fields
        ? documentField.template.fields
        : {};
    const errorsMetadata = {};

    Object.keys(metadataFields).forEach((key) => {
      const field = metadataFields[key];
      const value = file.metadataValues[`${key}_${file.documentFieldId}_IMPORT_templateField`];

      if (
        field.validations &&
        Array.isArray(field.validations) &&
        field.validations.find((v) => v.required)
      ) {
        if (value !== null && value !== undefined && value !== '') {
          delete errorsMetadata[`${key}_${file.documentFieldId}_IMPORT_templateField`];
        } else {
          errorsMetadata[`${key}_${file.documentFieldId}_IMPORT_templateField`] = Realize.i18n.t(
            'form.validations.required'
          );
        }
      } else {
        delete errorsMetadata[`${key}_${file.documentFieldId}_IMPORT_templateField`];
      }
    });

    if (file.successImport) {
      setCountFileImported(index + 1);
    } else if (
      hasErrorEmptyDocuments ||
      hasErrorAlreadyFileLinked ||
      Object.keys(errorsMetadata).length
    ) {
      let errorMessage = '';

      if (hasErrorEmptyDocuments) {
        errorMessage = Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyDocuments'
        );
      } else if (hasErrorAlreadyFileLinked) {
        errorMessage = Realize.i18n.t(
          'importMultipleModal.filesListErrors.alreadyFileLinked'
        );
      } else if (Object.keys(errorsMetadata).length) {
        errorMessage = `${Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyFieldsBegin'
        )} ${Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyFieldsMetadata'
        )} ${Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyFieldsEnd'
        )}`;
      }

      setFilesList(
        filesList.map((f) => {
          if (f.id === file.id) {
            return {
              ...f,
              error: { message: errorMessage },
              successImport: false,
              errorsMetadata,
            };
          }

          return f;
        })
      );

      setCountFileImported(index + 1);
    } else {
      const gridFormParent = documentField.inputProps.gridFormParent;
      const formApi = FormAPI.form(documentField.inputProps.formId);
      const fieldDocumentApi = gridFormParent
        ? formApi.fields(gridFormParent).fields(file.documentFieldId)
        : formApi.fields(file.documentFieldId);
      const metadataFields =
        documentField.template && documentField.template.fields
          ? documentField.template.fields
          : {};
      const metadataValues = file.metadataValues;

      DocumentActions.setSubmitDataField(
        file.documentFieldId,
        `${DocumentSubmitDataContainers.IMPORT}.file`,
        file.data
      );
      DocumentActions.setDocumentFileSelected(file.documentFieldId, file.data);

      for (const key in metadataFields) {
        const metadata = metadataFields[key];
        const findValue = metadataValues[`${key}_${file.documentFieldId}_IMPORT_templateField`];
        let value =
          findValue !== null && findValue !== undefined && findValue !== ''
            ? findValue
            : '';

        if (metadata.type === FormFieldTypes.CURRENCY)
          value = formatToNumber(value);
        if (metadata.type === FormFieldTypes.DATE)
          value = formatDatetime(value);
        if (
          metadata.type === FormFieldTypes.NUMBER &&
          typeof value === 'string'
        )
          value = value.replace(/[^-+\d]/g, '');

        DocumentActions.setSubmitMetadataField(
          documentField.inputId,
          'IMPORT',
          metadata.id,
          value
        );
      }

      DocumentActions.importDocument(file.documentFieldId, true);

      const subscribeSuccess = fieldDocumentApi.subscribe(
        FormEvents.DOCUMENT_IMPORT_SUCCESS,
        () => {
          subscribeSuccess.unsubscribe();

          if (subscribeError) {
            subscribeError.unsubscribe();
          }

          setFilesList(
            filesList.map((f) => {
              if (f.id === file.id) {
                return {
                  ...f,
                  error: {},
                  successImport: true,
                };
              }

              return f;
            })
          );

          setCountFileImported(index + 1);
        }
      );

      const subscribeError = fieldDocumentApi.subscribe(
        FormEvents.DOCUMENT_IMPORT_ERROR,
        (formId, inputId, error, response) => {
          subscribeError.unsubscribe();

          if (subscribeSuccess) {
            subscribeSuccess.unsubscribe();
          }

          let errorCode = '';
          let errorMessage = '';

          if (
            error === DocumentValidationsErrors.DUPLICATED_DOCUMENTS ||
            error === DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN ||
            error === DocumentValidationsErrors.CHECKOUT_UNDONE
          ) {
            errorMessage = error;
          } else {
            const objError =
              error &&
              error.entity &&
              error.entity.errors &&
              Array.isArray(error.entity.errors) &&
              error.entity.errors.length
                ? error.entity.errors[0]
                : {};

            if (
              objError.code === DocumentValidationsErrors.NULL_POINTER_EXCEPTION
            ) {
              errorMessage = Realize.i18n.t(
                'form.errors.null_pointer_exception'
              );
            } else if (objError.message) {
              errorMessage = objError.message;
              errorCode = objError.code;
            }
          }

          setFilesList(
            filesList.map((f) => {
              if (f.id === file.id) {
                return {
                  ...f,
                  error: { code: errorCode, message: errorMessage, response },
                  successImport: false,
                };
              }

              return f;
            })
          );

          setCountFileImported(index + 1);
        }
      );
    }
  };

  const clickError = (file) => {
    const { error, id } = file;

    if (error.message === DocumentValidationsErrors.DUPLICATED_DOCUMENTS) {
      const documentField = getDocumentStore(file.documentFieldId);
      const formApi = FormAPI.form(documentField.inputProps.formId);
      const gridFormParent = documentField.inputProps.gridFormParent;
      const fieldDocumentApi = gridFormParent
        ? formApi.fields(gridFormParent).fields(documentField.inputId)
        : formApi.fields(documentField.inputId);

      DocumentActions.errorDuplicatedDocuments(
        documentField.inputId,
        DocumentModalIds.IMPORT_DOCUMENT,
        error.response
      );

      const subscribeSuccess = fieldDocumentApi.subscribe(
        FormEvents.DUPLICATED_DOCUMENTS_SUCCESS,
        () => {
          subscribeSuccess.unsubscribe();

          setFilesList(
            filesList.map((f) => {
              if (f.id === id) {
                return {
                  ...f,
                  error: {},
                  successImport: true,
                };
              }

              return f;
            })
          );

          if (countFileSuccess() + 1 === filesList.length) {
            dismissModal();
          }
        }
      );
    }
  };

  const countFileSuccess = () => {
    return filesList.reduce((acc, cur) => {
      if (cur.successImport) {
        return acc + 1;
      }

      return acc;
    }, 0);
  };

  const keyUpElement = (e) => {
    if (e.which === 13 || e.keyCode === 13) {
      e.currentTarget.click();
    }
  };

  const getAssociatedFields = (documentFieldId) => {
    let documentField = fields[documentFieldId]

    if (!documentField) {
      const documentFieldStore = getDocumentStore(documentFieldId)
      const gridIdentifier = documentFieldStore.gridData.gridIdentifier
      const gridField = fields[gridIdentifier]

      documentField = gridField.fields.find((field) => {
        return field.id === documentFieldId
      })
    }

    const formFieldMappings = documentField.formFieldMappings
    const isSyncMetadataEnabled = documentField.syncMetadata
    const hasNoAssociatedsMetadata = formFieldMappings === undefined

    if (!isSyncMetadataEnabled || hasNoAssociatedsMetadata) {
      return []
    }

    const associatedFieldNames = formFieldMappings.map((field) => {
      return field.documentField
    })

    return associatedFieldNames
  }

  const handleChangeMetadataValues = ({ id, documentFieldId, key, fieldValue }) => {
    const dataFieldValue = Array.isArray(fieldValue) ? fieldValue[0] : fieldValue

    const fileList = filesList.map((f) => {
      if (f.id === id) {
        return {
          ...f,
          metadataValues: {
            ...f.metadataValues,
            [`${key}_${documentFieldId}_IMPORT_templateField`]: dataFieldValue,
          },
        };
      }

      return f;
    })

    setFilesList(fileList);

    return false;
  }

  const getFieldsMetadata = (id, documentFieldId) => {
    const documentField = getDocumentStore(documentFieldId);
    const metadataFields = documentField.template?.fields
        ? documentField.template.fields
        : {};

    let metafields = {};

    Object.keys(metadataFields).forEach((key) => {
      metafields[key] = {
        ...metadataFields[key],
        id: `${key}_${documentFieldId}_IMPORT_templateField`,
        required:
          metadataFields[key].validations &&
          Array.isArray(metadataFields[key].validations) &&
          !!metadataFields[key].validations.find((v) => v.required),
        onChange: (_, fieldValue) => handleChangeMetadataValues({
          id,
          documentFieldId,
          key,
          fieldValue,
        })
      };
    });

    return metafields;
  };

  useEffect(() => {
    if (startQueue && countFileImported + 1 <= filesList.length) {
      importFileByQueue(countFileImported);
    } else {
      if (startQueue) {
        setStartQueue(false);
        setCountFileImported(0);

        if (countFileSuccess() === filesList.length) {
          dismissModal();
        }
      }
    }
  }, [startQueue, countFileImported]);

  useEffect(() => {
    if (acceptedFiles.length) {
      const filesMapped = acceptedFiles.map((f) => ({
        id: generateUUID(),
        data: f,
        error: {},
        metadataValues: {},
      }));
      setFilesList([...filesList, ...filesMapped]);
    }
  }, [acceptedFiles]);

  return (
    <div className={`container-import-multiple-modal ${isOpen ? 'open' : ''}`}>
      <Realize.Modal
        id="importMultipleModal"
        key="importMultipleModal"
        dismissible={false}
        ready={modalReady}
      >
        <Realize.ModalHeader>{renderModalHeader()}</Realize.ModalHeader>
        <Realize.ModalContent>
          <div className="modal-container-import-multiple">
            <FlashMessages id="flash-modal-import-multiple" />

            <div
              {...getRootProps({
                style,
                className: `base-style drop-zone ${
                  startQueue ? 'disabled' : ''
                }`,
              })}
            >
              <input data-testid='file-input' {...getInputProps()} />
              <p>{Realize.i18n.t('importMultipleModal.dropZone')}</p>
            </div>

            {!!filesList.length && (
              <ul className="files">
                {filesList.map((file) => (
                  <li
                    key={file.id}
                    className={
                      hasMetadata(file.documentFieldId) ? 'has-fields' : ''
                    }
                  >
                    {file.successImport && (
                      <div className="block-success"></div>
                    )}
                    <div className="container-file">
                      {renderSelectDocumentFields(
                        file.id,
                        file.documentFieldId
                      )}

                      <i className="material-icons attach-file">attach_file</i>

                      <span className={`file-name`} title={file.data.name}>
                        {file.data.name}
                      </span>

                      <div className={`container-actions file-id-${file.id}`}>
                        {startQueue && !file.successImport ? (
                          <span class="loader-import"></span>
                        ) : file.successImport ? (
                          <i
                            className="material-icons success-import"
                            title={Realize.i18n.t(
                              'importMultipleModal.successImport'
                            )}
                          >
                            check
                          </i>
                        ) : (
                          <>
                            {file.error && file.error.message && (
                              <Popover
                                isOpen={getPopoverIsOpen(file.id)}
                                positions={['left', 'top', 'bottom', 'right']}
                                content={contentPopoverError(file)}
                                parentElement={document.querySelector(
                                  `#importMultipleModal .container-actions.file-id-${file.id}`
                                )}
                              >
                                <i
                                  className="material-icons alert-error"
                                  onMouseOver={() =>
                                    changePopoversOpen(file.id, true)
                                  }
                                  onMouseOut={() =>
                                    changePopoversOpen(file.id, false)
                                  }
                                  onClick={() => clickError(file)}
                                  tabIndex="0"
                                  onKeyUp={keyUpElement}
                                >
                                  error_outline
                                </i>
                              </Popover>
                            )}
                            <i
                              className="material-icons remove-file"
                              onClick={() => removeFile(file.id)}
                              title={Realize.i18n.t(
                                'importMultipleModal.removeFile'
                              )}
                              tabIndex="0"
                              onKeyUp={keyUpElement}
                            >
                              remove_circle_outline
                            </i>
                          </>
                        )}
                      </div>

                      {hasMetadata(file.documentFieldId) && (
                        <div className="container-fields">
                          <div
                            className={`open-fields ${
                              file.metadataVisible ? 'is-open' : ''
                            }`}
                            onClick={() => toggleVisibleMetadata(file.id)}
                            tabIndex="0"
                            onKeyUp={keyUpElement}
                          >
                            {file.metadataVisible ? (
                              <i className="material-icons">expand_less</i>
                            ) : (
                              <i className="material-icons">expand_more</i>
                            )}

                            <span className="title">
                              {Realize.i18n.t(
                                'importMultipleModal.metadataFields'
                              )}
                            </span>
                          </div>

                          <div
                            className={`content-fields ${
                              file.metadataVisible ? 'is-open' : ''
                            }`}
                          >
                            <InputGroup
                              inputs={getFieldsMetadata(
                                file.id,
                                file.documentFieldId
                              )}
                              data={file.metadataValues}
                              errors={file.errorsMetadata}
                              metadataFieldsToDisable={getAssociatedFields(
                                file.documentFieldId
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Realize.ModalContent>
        <Realize.ModalFooter className={'right-align'}>
          {renderModalButtons()}
        </Realize.ModalFooter>
      </Realize.Modal>
    </div>
  );
});

export default ImportMultipleModal;
